import type { Template } from '@brush/api-client';
import { FileCode, FileText, Plus, Table2 } from 'lucide-react';
import { useState } from 'react';
import { featureLabel } from '../../features';
import { PURPOSES } from '../../purposes';
import { useTemplates } from '../../queries';
import { TemplateModal, type TemplateIntent } from '../TemplateModal';
import { AreaGate } from './AreaGate';

/**
 * A free name for a copy of <paramref name="source"/>.
 *
 * Names are unique platform-wide, so "X (copy)" fails the second time you duplicate the same
 * template — a guaranteed error on an ordinary action. Walking up to "(copy 2)" costs nothing
 * here, where the whole catalogue is already in hand.
 *
 * Still only a suggestion: the field is editable, and the server's unique index remains the
 * thing that actually decides. Comparison is case-insensitive to match SQL Server's collation,
 * which is what the server compares with.
 */
function suggestCopyName(source: Template, existing: Template[]): string {
  const taken = new Set(existing.map((t) => t.name.trim().toLowerCase()));
  const base = `${source.name} (copy`;

  for (let n = 1; n < 100; n++) {
    const candidate = n === 1 ? `${base})` : `${base} ${n})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }

  // Ninety-nine copies of one template is not a case worth designing for; let the server answer.
  return `${base} ${existing.length + 1})`;
}

/**
 * The platform's template catalogue: defined once here, filled in per room.
 *
 * The rows are the definitions, not the content — a room's HTML or field order is its own and
 * is edited in the room console.
 */
export function TemplatesArea() {
  const templates = useTemplates();
  // One state for one modal; `null` is closed. What it was opened to do is on the intent itself
  // rather than inferred from which of several optional props got set.
  const [intent, setIntent] = useState<TemplateIntent | null>(null);

  const rows = templates.data ?? [];

  return (
    <AreaGate
      queries={[templates]}
      loadingLabel="Loading templates…"
      errorMessage="Couldn't load the templates."
    >
      <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
        <header className="flex items-start justify-between border-b border-ink-800/10 px-6 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-display font-semibold text-ink-950">
              <FileText className="size-4 text-gold-500" /> Templates
            </h2>
            <p className="mt-1 text-xs text-ink-800/60">
              Rooms supply their own content for each entry. Templates with no features listed go
              to every room; the rest reach only rooms running at least one of the features listed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIntent({ mode: 'create' })}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900"
          >
            <Plus className="size-4" /> New template
          </button>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
              {/* The id is a GUID and carries no meaning to an operator, so it stays off the
                  screen — it is the client's routing handle, not information. */}
              <tr>
                <th className="px-6 py-3 font-medium">Template</th>
                <th className="px-6 py-3 font-medium">Type</th>
                <th className="px-6 py-3 font-medium">Used for</th>
                <th className="px-6 py-3 font-medium">Available to</th>
                <th className="px-6 py-3 font-medium">Fields</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-800/5">
              {rows.map((template) => (
                <tr key={template.id} className="hover:bg-felt-50/30">
                  <td className="px-6 py-4 font-medium text-ink-950">{template.name}</td>
                  <td className="px-6 py-4">
                    <KindPill kind={template.kind} />
                  </td>
                  <td className="px-6 py-4">
                    <PurposeCell purpose={template.purpose} />
                  </td>
                  <td className="px-6 py-4">
                    <FeatureGate features={template.features} />
                  </td>
                  <td className="px-6 py-4 text-ink-800/70">
                    {template.kind === 'csv' ? (
                      <>
                        {template.fields.length}
                        <span className="text-ink-800/40">
                          {' '}
                          ({template.fields.filter((f) => f.required).length} required)
                        </span>
                      </>
                    ) : (
                      <span className="text-ink-800/40">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setIntent({
                            mode: 'clone',
                            source: template,
                            suggestedName: suggestCopyName(template, rows),
                          })
                        }
                        className="text-xs font-medium text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
                      >
                        Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => setIntent({ mode: 'edit', template })}
                        className="text-xs font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-ink-800/60">
                    No templates yet. Create one and every room can start filling it in.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {intent ? (
        <TemplateModal
          // Remounts per intent, so a draft never carries from one template to the next.
          key={intent.mode === 'create' ? 'new' : `${intent.mode}:${intent.mode === 'edit' ? intent.template.id : intent.source.id}`}
          intent={intent}
          playersTemplateExists={rows.some((t) => t.purpose === 'import_players')}
          onClose={() => setIntent(null)}
        />
      ) : null}
    </AreaGate>
  );
}

/**
 * Who the template reaches. "All rooms" is stated rather than left blank: an empty cell reads
 * as missing data, when in fact no feature named is a deliberate and common choice.
 */
function FeatureGate({ features }: { features: readonly string[] }) {
  if (features.length === 0) {
    return <span className="text-xs text-ink-800/40">All rooms</span>;
  }

  return (
    <span className="flex flex-wrap gap-1">
      {features.map((feature) => (
        <span
          key={feature}
          className="inline-flex items-center rounded-md bg-felt-50 px-2 py-1 text-xs font-medium text-felt-900"
        >
          {featureLabel(feature)}
        </span>
      ))}
    </span>
  );
}

/**
 * The job the platform puts the template to. "General" is stated in words rather than shown as a
 * dash: it is a choice an administrator made, and the same cell has to distinguish it from a
 * purpose this build does not know — a template created by a newer catalogue reads as its raw
 * literal rather than as nothing at all.
 */
function PurposeCell({ purpose }: { purpose: string }) {
  const known = PURPOSES.find((p) => p.value === purpose);
  if (!known) return <span className="font-mono text-xs text-ink-800/50">{purpose}</span>;

  const Icon = known.icon;
  return (
    <span
      className={
        purpose === 'general'
          ? 'inline-flex items-center gap-1.5 text-xs text-ink-800/50'
          : 'inline-flex items-center gap-1.5 rounded-md bg-gold-500/10 px-2 py-1 text-xs font-medium text-ink-950'
      }
    >
      <Icon className="size-3.5" /> {known.label}
    </span>
  );
}

function KindPill({ kind }: { kind: string }) {
  const csv = kind === 'csv';
  const Icon = csv ? Table2 : FileCode;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-ink-800/5 px-2 py-1 text-xs font-medium text-ink-800/70">
      <Icon className="size-3.5" /> {csv ? 'CSV' : 'HTML'}
    </span>
  );
}
